import React from 'react'
import { X, Download } from 'lucide-react'
import { jsPDF } from 'jspdf'
import { RoleWithMember } from '../../services/orgChartService'

interface PositionDescriptionModalProps {
  isOpen: boolean
  onClose: () => void
  role: RoleWithMember
  /** Display label for who this role reports to, or null if top of chart. */
  reportsToLabel: string | null
}

function buildFilename(title: string): string {
  const safe = title
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `Position-Description-${safe || 'Role'}.pdf`
}

export const PositionDescriptionModal: React.FC<PositionDescriptionModalProps> = ({
  isOpen,
  onClose,
  role,
  reportsToLabel
}) => {
  if (!isOpen) return null

  const accountabilities = role.accountabilities || []
  const teams = role.teams?.length
    ? role.teams
    : role.inheritedTeams?.length
      ? role.inheritedTeams
      : []
  const holder = role.assignedMember?.name.fullName || null
  const reportsTo = reportsToLabel || 'None (top of organisation)'

  const handleDownloadPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    const margin = 56
    const pageWidth = doc.internal.pageSize.getWidth()
    const maxWidth = pageWidth - margin * 2
    let y = margin

    const ensureSpace = (needed: number) => {
      const pageHeight = doc.internal.pageSize.getHeight()
      if (y + needed > pageHeight - margin) {
        doc.addPage()
        y = margin
      }
    }

    const addHeading = (text: string, size = 18) => {
      ensureSpace(size + 16)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(size)
      doc.text(text, margin, y)
      y += size + 12
    }

    const addLabelValue = (label: string, value: string) => {
      ensureSpace(36)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text(label, margin, y)
      y += 16
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(11)
      const lines = doc.splitTextToSize(value, maxWidth)
      ensureSpace(lines.length * 14 + 8)
      doc.text(lines, margin, y)
      y += lines.length * 14 + 12
    }

    addHeading('Position Description')
    addLabelValue('Role', role.title)
    addLabelValue('Currently held by', holder || 'Vacant')
    addLabelValue('Reports to', reportsTo)
    addLabelValue(
      'Teams',
      teams.length > 0 ? teams.join(', ') : 'None assigned'
    )

    ensureSpace(28)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.text('Accountabilities', margin, y)
    y += 18

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    if (accountabilities.length === 0) {
      ensureSpace(16)
      doc.text('None listed', margin, y)
      y += 14
    } else {
      for (const item of accountabilities) {
        const bulletLines = doc.splitTextToSize(`• ${item}`, maxWidth)
        ensureSpace(bulletLines.length * 14 + 4)
        doc.text(bulletLines, margin, y)
        y += bulletLines.length * 14 + 4
      }
    }

    doc.save(buildFilename(role.title))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Position Description</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Role</p>
            <p className="text-base font-semibold text-gray-900 mt-0.5">{role.title}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Currently held by
            </p>
            <p className="text-sm text-gray-800 mt-0.5">
              {holder || <span className="italic text-gray-400">Vacant</span>}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Reports to
            </p>
            <p className="text-sm text-gray-800 mt-0.5">{reportsTo}</p>
          </div>

          {teams.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Teams
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {teams.map(teamName => (
                  <span
                    key={teamName}
                    className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700"
                  >
                    {teamName}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Accountabilities
            </p>
            {accountabilities.length > 0 ? (
              <ul className="space-y-1.5">
                {accountabilities.map((acc, idx) => (
                  <li key={idx} className="text-sm text-gray-800 flex gap-2">
                    <span className="text-gray-400 flex-shrink-0">•</span>
                    <span>{acc}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-400 italic">None listed</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleDownloadPdf}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  )
}
